/**
 * Enable IntersectionObserver on document zone
 */
class IntersectionObserver_JS {
    /**
    * @constructor
    * @param {IntersectionObserverInit} options  optional parameters
    * See "Intersection observer options" from {@link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#intersection_observer_concepts_and_usage}
    */
    constructor(options) {
        const _options = {
            root: null,
            rootMargin: "0px",
            threshold: 1.0,
            ...options
        };

        /**
         * IntersectionObserver object
         * @type {IntersectionObserver}
         */
        this.IntersectionObserver = null;

        this.init(_options);
    }
    /**
     * Initialize class 
     * @private
     * @param {IntersectionObserverInit} options Intersection observer options
    */
    init(options) {

        if (!("IntersectionObserver" in window)) {
            console.error('IntersectionObserver is not enabled in this browser.');
            return;
        }
        this.IntersectionObserver = new IntersectionObserver(this.callback.bind(this), options);
    }
    /**
     * each scan by IntersectionObserver raise this method
     * @private
     * @param {Array.<IntersectionObserverEntry>} entries array of elements to be observed
     * @param {IntersectionObserver} observer 
     */
    callback(entries, observer) {
        entries.forEach(e => this.intersectingOnEntry(e));
    }
    /**
     * validate each entry
     * @private
     * @param {IntersectionObserverEntry } entry observed element
     */
    intersectingOnEntry(entry) {
        if (entry.isIntersecting) {     // entry is visible base on threshold option
            const objObserved = entry.target;  // get observed entry
            objObserved.IntersectingAction();  // execute an entry action
            if (objObserved.autoUnObserve) {   // if the action must execute one time
                this.IntersectionObserver.unobserve(objObserved);
            }
        }
    }
    /**
     * Register a DOM element to be observed
     * @public
     * @param {HTMLElement} element DOM element
     * @param {Function} fnIntersectingAction  Action to be executed once the observed DOM element is visible
     * @param {Boolean} autoUnObserve true to execute an action only one time
     * @example
     * //initialize object
     * // The options area optional
     * const objIntObs = new IntersectionObserver_JS({
            *   root: null,
                rootMargin: "0px",
                threshold: 1.0
     * });
     * // Register an observed DOM element
     * const DOMObject = document.querySelector('#observedDOM');
     * const fnIntersectingAction = () => ...
     * objIntObs.registerElement(DOMObject, fnIntersectingAction, false);
     */
    registerElement(element, fnIntersectingAction, autoUnObserve) {
        if ((element instanceof HTMLElement) && typeof fnIntersectingAction == 'function') {
            element.IntersectingAction = fnIntersectingAction;
            element.autoUnObserve = !!autoUnObserve;
            this.IntersectionObserver.observe(element);
        }
        else {
            console.warn(`IntersectionObserver_JS : ${element} cannot be observable. Verify parameters.`);
        }
    }
    /**
     * Register DOM elements in IntersectionObserver
     * @public
     * @param {String} selectorAll DOM querySelectorAll parameter
     * @param {Function} fnIntersectingAction  Action to be executed once the observed DOM element is visible
     * @param {Boolean} autoUnObserve true to execute an action only one time
     * @example
     * //initialize object
     * // The options area optional
     * const objIntObs = new IntersectionObserver_JS({
            *   root: null,
                rootMargin: "0px",
                threshold: 1.0
     * });
     * // Register an observed DOM element
     * const selector = '.observedDOMs';
     * const fnIntersectingAction = () => ...
     * // register a DOM and once is viewed is unregister
     * objIntObs.registerSelectorAll(selector, fnIntersectingAction, true);
     */
    registerSelectorAll(selectorAll, fnIntersectingAction, autoUnObserve) {
        const _DOMObjects = document.querySelectorAll(selectorAll);
        if (_DOMObjects.length > 0 && typeof fnIntersectingAction == 'function') {
            _DOMObjects.forEach(elem => {
                elem.IntersectingAction = fnIntersectingAction;
                elem.autoUnObserve = !!autoUnObserve;
                this.IntersectionObserver.observe(elem);
            });
        }
        else {
            console.warn(`IntersectionObserver_JS : ${selectorAll} selector cannot be observable. Verify parameters.`);
        }
    }
    /**
     * remove a DOM object from IntersectionObserver Object 
     * @public
     * @param {HTMLElement} element DOM element
     */
    unRegisterElement(element) {
        if (element instanceof HTMLElement) {
            this.IntersectionObserver.unobserve(element);
        }
    }
    /**
     * remove DOM object(s) from IntersectionObserver Object 
     * @public
     * @param {string} selectorAll  DOM querySelectorAll parameter
     */
    unRegisterSelector(selectorAll) {
        const _DOMObjects = document.querySelectorAll(selectorAll);
        if (_DOMObjects.length > 0) {
            _DOMObjects.forEach(elem => this.IntersectionObserver.unobserve(elem));
        }
    }
    /**
     * Static instance for use in the entire document without the need to instantiate a new class.
     * @public
     * @type {IntersectionObserver_JS}
     * @example
     * IntersectionObserver_JS.Instance.registerSelectorAll(selector, fnIntersectingAction, true)
     */
    static Instance = new IntersectionObserver_JS();
}